import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges,
    OnInit,
    Output, SimpleChange
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { Log } from "ng2-logger";

import * as _ from "lodash";
import { TranslateService } from '@ngx-translate/core';
import { AppUtils } from "@app/shared/util/app.utils";


// TODO the mystic dropdown triggers an selectItem event when introduced in a form. Thus, it causes havoc and needs to be fixed.
// A bug that comes out of this is the filterGroupChange event being triggered on the table, which, loads items multiple times.
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-dropdown-autocomplete',
    templateUrl: './dropdown-autocomplete.component.html',
    styleUrls: ['./dropdown-autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DropdownAutocompleteComponent,
            multi: true
        }
    ]
})
export class DropdownAutocompleteComponent implements ControlValueAccessor, OnChanges, OnInit {
    _logger = Log.create(DropdownAutocompleteComponent.name);

    @Input() labelAlign: 'top' | 'left' | 'floating' = 'top';
    @Input() label = '';
    @Input() displayField = 'name';
    @Input() valueField = 'id';
    @Input() emptyText = 'No empty text provided';
    @Input() appendTo = '';
    @Input() items = [];
    @Input() formControl: UntypedFormControl;
    @Input() isNgModel = false;
    @Input() showErrors = false;
    @Input() required = false;
    @Input() dropdown = true;
    @Input() useObjects = true;
    @Input() labelStyle = {};
    @Input() isFirstSelectable = true;
    @Input() canAddOther = false;
    @Input() otherValue: string;
    @Input() otherField = 'other';
    @Input() otherPlaceholder = 'Other';
    @Input() hasInputForOther = false;
    @Input() id = AppUtils.generateUUID();
    @Input() resetValueOnOpen = false;
    @Input() @HostBinding('class.disabled') disabled = false;

    // TODO this is a hack, because the dropdown currently resets it's value each time anything is updated.
    @Input() resetSelectionOnItemChange = true;
    @HostBinding('class.m2') @Input() m2 = false;

    @Output() selectItem = new EventEmitter();
    @Output() searchChange = new EventEmitter<string>();

    filteredItems: any[];
    private alreadySelected = false;

    private selectedItem: any = {};
    private form: UntypedFormGroup;
    private onChangeCallback: (_: any) => {};
    private onToucheCallback: (_: any) => {};

    @HostBinding('class') get labelAlignmentClass(): string {
        if (this.labelAlign === 'left') {
            return 'label-left';
        } else if (this.labelAlign === 'top') {
            return 'label-top';
        } else {
            return 'label-float.float-input'
        }
    }

    get hasError() {
        return this.formControl && this.formControl.errors && this.showErrors;
    }

    constructor(private translate: TranslateService, private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        if (this.canAddOther) {
            const other = this.otherValue || this.formControl.value ? this.formControl.value[this.otherField] : '';
            this.form = new UntypedFormGroup({
                other: new UntypedFormControl(other, Validators.required)
            });
        }
    }

    ngOnChanges(changes: { [propName: string]: SimpleChange }) {
        if (!this.isNgModel && changes['items'] && this.resetSelectionOnItemChange) {
            const clearSelection = !!((changes['items'].currentValue &&
                    changes['items'].currentValue.length === 0 &&
                    changes['items'].previousValue &&
                    changes['items'].previousValue.length !== 0) ||
                ((changes['items'].currentValue && changes['items'].previousValue) &&
                    (changes['items'].currentValue[0] && changes['items'].previousValue[0]) &&
                    !_.isEqual(changes['items'].currentValue[0].id, changes['items'].previousValue[0].id)));
            if (clearSelection) {
                this.selectedItem = {};
            }
        }
    }

    get value() {
        if (this.form && this.canAddOther && this.isOtherSelected()) {
            this.selectedItem[this.otherField] = this.form.value.other;
        }

        return this.selectedItem;
    }

    set value(value) {
        this.selectedItem = value;
    }

    get suggestions() {
        if (this.canAddOther && !this.items.find(item => item[this.valueField] === 0)) {
            this.items = [...this.items, {
                [this.valueField]: 0,
                [this.displayField]: this.translate.instant('generic.dropdown.other')
            }];
        }

        return this.filteredItems;
    }

    writeValue(value) {
        if (this.isNgModel) {
            if (value !== undefined && value !== null && this.items) {
                this._logger.info('writeValue:: ', value);
                this.updateSelectedItem(this.items.find(x => x.id === value));
            }
        } else {
            this.updateSelectedItem(value);
        }
        this.cdr.markForCheck();
    }

    onItemSelect(value) {
        this.alreadySelected = false;
        this.updateSelectedItem(value);
        this.alreadySelected = true;
        if (this.onChangeCallback) {
            this.onChangeCallback(value && !this.useObjects ? value[this.valueField] : value);
        }
        this._logger.info('onItemSelect:: ', this.selectedItem);
    }

    onClear() {
        this.selectedItem = {};
        if (this.onChangeCallback) {
            this.onChangeCallback(null);
        }
        this.cdr.markForCheck();
    }

    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn) {
        this.onToucheCallback = fn;
    }

    search(event) {
        this.searchChange.emit(event.query);

        // Filter items based on the input query
        this.filteredItems = this.items.filter(item => {
            return item[this.displayField] && item[this.displayField].toString().toLowerCase().includes(event.query.toLowerCase());
        });

        if (this.resetValueOnOpen) {
            // Reset the selected value when the dropdown is used
            this.value = null;  // Reset the value
            this.selectedItem = {}; // Reset the selectedItem

            // Call onChangeCallback to propagate changes
            if (this.onChangeCallback) {
                this.onChangeCallback(null);  // Notify form control of reset
            }
        }
    }

    updateSelectedItem(item) {
        const currentValue = this.selectedItem && this.selectedItem[this.valueField];
        const nextValue = item && item[this.valueField];
        const isSameSelection = currentValue !== undefined && nextValue !== undefined
            && String(currentValue) === String(nextValue);

        if (this.alreadySelected && isSameSelection) {
            this.alreadySelected = false;
            return;
        }

        this.alreadySelected = false;
        this.selectedItem = this.items && this.items.length > 0 && item && item.name === this.items[0].name
        && !this.isFirstSelectable ? {} : item;
        if (this.canAddOther && this.isOtherSelected()) {
            this.selectedItem[this.otherField] = this.form.value.other;
        }
        if (this.canAddOther && !this.isOtherSelected() && this.form) {
            this.form.reset();
        }

        this.selectItem.emit(this.selectedItem);
        this.cdr.markForCheck();
    }

    isOtherSelected() {
        return this.selectedItem && this.selectedItem[this.valueField] === 0;
    }

    get leftLabel() {
        return this.label && this.labelAlign === 'left';
    }

    get topLabel() {
        return this.label && this.labelAlign === 'top';
    }

    get floatLabel() {
        return this.label && this.labelAlign === 'floating';
    }
}
